// @flow strict

import React, { useState } from 'react';
import { Grid } from '@material-ui/core';
import { Link } from 'gatsby';
import type { PostNode } from '~types/post';

import * as styles from './post-list.module.less';
import ArrowIcon from '@material-ui/icons/KeyboardArrowRightOutlined';
import ClearIcon from '@material-ui/icons/Clear';
import type { GlossaryCategory } from '~/types/glossary-category';

import clsx from 'clsx';
import { useLanguageContext } from '~contexts/language-context';
import { GatsbyImage } from 'gatsby-plugin-image';

type PostLinkProps = {
  posts: Array<PostNode>,
  categories: Array<GlossaryCategory>,
};

export const PostList = ({ posts, categories }: PostLinkProps) => {
  const [activeCategoryFilter, setActiveCategoryFilter] =
    useState<GlossaryCategory | null>(null);

  const { loc } = useLanguageContext();

  const clearCategoryFilter = () => {
    setActiveCategoryFilter(null);
  };

  const setCategoryFilter = (category: GlossaryCategory) => {
    if (activeCategoryFilter?.category === category?.category) {
      clearCategoryFilter();
      return;
    }

    setActiveCategoryFilter(category);
  };

  const isCategoryFilterActive = (category: GlossaryCategory) => {
    return activeCategoryFilter?.category === category.category;
  };

  return (
    <React.Fragment>
      <div className={styles.categoryFilterContainer}>
        {activeCategoryFilter && (
          <button className={styles.clearButton} onClick={clearCategoryFilter}>
            <span>
              <ClearIcon className={styles.clearButton__clearIcon} />
              <span className={styles.clearButton__clearButtonText}>
                {loc('_glossary_clear_filter')}
              </span>
            </span>
          </button>
        )}
        <Grid container spacing={3}>
          {categories.map((category) => (
            <Grid item xs={12} md={4} key={category.category}>
              <button
                className={clsx(styles.categoryButton, {
                  [styles.isActive]: isCategoryFilterActive(category),
                })}
                onClick={() => setCategoryFilter(category)}
              >
                <span>
                  <GatsbyImage
                    image={
                      category.icons.active.src.childImageSharp.gatsbyImageData
                    }
                    alt={`${category.name} Icon`}
                    className={clsx(styles.categoryButton__categoryIcon, {
                      [styles.hidden]: !isCategoryFilterActive(category),
                    })}
                  />
                  <GatsbyImage
                    image={
                      category.icons.inactive.src.childImageSharp
                        .gatsbyImageData
                    }
                    alt={`${category.name} Icon`}
                    className={clsx(styles.categoryButton__categoryIcon, {
                      [styles.hidden]: isCategoryFilterActive(category),
                    })}
                  />
                  {category.name}
                </span>
              </button>
            </Grid>
          ))}
        </Grid>
      </div>
      <div>
        <h2 className={styles.postListHeadline}>
          {activeCategoryFilter
            ? activeCategoryFilter.name
            : loc('_glossary_all_articles')}
        </h2>
        <Grid container spacing={6}>
          {posts.map((post) => {
            if (
              activeCategoryFilter !== null &&
              activeCategoryFilter?.category !== post.frontmatter.category
            ) {
              return null;
            }

            return (
              <Grid item xs={12} key={post.id}>
                <Link href={post.frontmatter.permalink} className={styles.link}>
                  <span className={styles.linkText}>
                    {post.frontmatter.title}
                  </span>
                  <ArrowIcon className={styles.arrowIcon} />
                </Link>
              </Grid>
            );
          })}
        </Grid>
      </div>
    </React.Fragment>
  );
};
